import type { InitialResponseV5, ScheduledPlay } from '@/types/initial-request';

export type SubscriptionLimits = {
  eventLimit: number;
  smsLimit: number;
  playersPerSchedule: number;
  maxCourtsPerEvent: number;
  maxRoundRobinsPerMonth: number;
  maxPlayersPerRoundRobins: number;
  maxSdEliminationTournamentsPerMonth: number;
  maxPlayersPerSdEliminationTournaments: number;
};

export type SubscriptionUsage = Record<string, number>;

export type SubscriptionLimitKey = 'sms_limit' | 'players_per_schedule' | 'event_limit';

export const DEFAULT_SUBSCRIPTION_LIMITS: SubscriptionLimits = {
  eventLimit: 1,
  smsLimit: 15,
  playersPerSchedule: 5,
  maxCourtsPerEvent: 1,
  maxRoundRobinsPerMonth: 1,
  maxPlayersPerRoundRobins: 8,
  maxSdEliminationTournamentsPerMonth: 0,
  maxPlayersPerSdEliminationTournaments: 0,
};

export function buildSubscriptionUsage(data?: InitialResponseV5): SubscriptionUsage {
  const usage: SubscriptionUsage = {};
  (data?.userActivityUsageMonthly ?? []).forEach((entry) => {
    const key = entry.activityType?.trim();
    if (!key) return;
    const value = entry.activityCount ?? 0;
    usage[key] = Math.max(usage[key] ?? 0, value);
  });
  return usage;
}

export function getActivityUsage(usage: SubscriptionUsage, featureKey: string) {
  return usage[featureKey] ?? 0;
}

export function isExceededSMSLimit(usage: SubscriptionUsage, limits: SubscriptionLimits) {
  return getActivityUsage(usage, 'sms_limit') >= limits.smsLimit;
}

export function isExceededPlayersPerSchedule(limits: SubscriptionLimits, playersInvited: number) {
  return playersInvited > limits.playersPerSchedule;
}

export function isExceededEventLimit(
  plays: ScheduledPlay[] | undefined,
  scheduleId: string | undefined,
  limits: SubscriptionLimits,
) {
  if (!plays?.length) return false;
  let eventCount = 0;
  plays.forEach((play) => {
    if (scheduleId && play.scheduleId === scheduleId) return;
    if (play.invitations && play.invitations.length > 0) {
      eventCount += 1;
    }
  });
  return eventCount >= limits.eventLimit;
}
